import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaEdit, FaTrash, FaUserInjured, FaSearch, FaPhone, FaTint, FaUserMd, FaArrowLeft } from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { useApp, useAutoRefresh } from '../context/AppContext';
import { getPatients, createPatient, updatePatient, deletePatient, getDoctors, getRegisteredPatients, getFileUrl } from '../services/api';

const EMPTY_FORM = {
  name: '', age: '', gender: 'Male', phone: '', email: '',
  address: '', bloodGroup: 'O+', medicalHistory: '', doctorId: ''
};

const Patients = () => {
  const { showToast, user } = useApp();
  const role = user?.role || 'Doctor';
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewPatient, setViewPatient] = useState(null);
  const [registeredPatients, setRegisteredPatients] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerSelected, setPickerSelected] = useState('');

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const [patRes, docRes, regRes] = await Promise.allSettled([
        getPatients(),
        role === 'Admin' ? getDoctors() : Promise.resolve({ data: { data: [] } }),
        (role === 'Doctor' || role === 'Admin') ? getRegisteredPatients() : Promise.resolve({ data: { data: [] } }),
      ]);
      setPatients(patRes.status === 'fulfilled' ? (patRes.value.data?.data || []) : []);
      if (docRes.status === 'fulfilled') setDoctors(docRes.value.data?.data || []);
      if (regRes.status === 'fulfilled') setRegisteredPatients(regRes.value.data?.data || []);
    } catch {
      setPatients([]);
    }
    setLoading(false);
  }, [role]);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);
  useAutoRefresh(fetchPatients, 'patients');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.doctorId) delete payload.doctorId;
      if (editId) {
        const res = await updatePatient(editId, payload);
        setPatients(prev => prev.map(p => (p.id === editId || p._id === editId) ? (res.data?.data || { ...p, ...payload }) : p));
        showToast('Patient updated!', 'success');
      } else {
        const res = await createPatient(payload);
        setPatients(prev => [...prev, res.data?.data || res.data]);
        showToast('Patient added!', 'success');
      }
    } catch (err) {
      showToast(err.message || 'Failed to save', 'error');
    }
    setSaving(false);
    setShowModal(false);
    setForm(EMPTY_FORM);
    setEditId(null);
  };

  const handleEdit = (p) => {
    setForm({
      name: p.name, age: p.age, gender: p.gender, phone: p.phone,
      email: p.email || '', address: p.address || '', bloodGroup: p.bloodGroup,
      medicalHistory: p.medicalHistory || '', doctorId: p.doctorId || ''
    });
    setEditId(p.id || p._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this patient?')) return;
    try {
      await deletePatient(id);
      setPatients(prev => prev.filter(p => p.id !== id && p._id !== id));
      showToast('Patient deleted', 'error');
    } catch (err) { showToast(err.message || 'Delete failed', 'error'); }
  };

  const handlePickerConfirm = () => {
    if (pickerSelected) {
      const p = registeredPatients.find(rp => String(rp.id || rp._id) === pickerSelected);
      if (p) {
        setForm({
          name: p.name || '',
          age: p.age || '',
          gender: p.gender || 'Male',
          phone: p.phone || '',
          email: p.email || '',
          address: p.address || '',
          bloodGroup: p.bloodGroup || 'O+',
          medicalHistory: p.medicalHistory || '',
          doctorId: p.doctorId || '',
        });
      }
    } else {
      setForm(EMPTY_FORM);
    }
    setShowPicker(false);
    setEditId(null);
    setShowModal(true);
  };

  const getDoctorName = (doctorId) => {
    if (!doctorId) return null;
    // A doctor viewing their own Patients page never fetches the full doctor
    // list (only Admin does, above) — every row here is their own patient,
    // so doctorId is always their own id. Resolve it directly instead of
    // falling through to the "Dr. #4" id fallback.
    if (role === 'Doctor' && doctorId === user?.id) return user.name;
    return doctors.find(d => d.id === doctorId)?.name || `Dr. #${doctorId}`;
  };

  const filtered = patients.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.phone?.includes(search) ||
    p.bloodGroup?.toLowerCase().includes(search.toLowerCase())
  );

  if (showModal) {
    return (
      <div className="page-wrapper">
        <Header title={editId ? 'Edit Patient' : 'Add Patient'} />
        <Sidebar />
        <main className="page-content">
          <div className="page-header">
            <div>
              <h1><FaUserInjured style={{ color: 'var(--primary)', marginRight: 8 }} />{editId ? 'Edit Patient' : 'Add Patient'}</h1>
              <p>{editId ? 'Update patient information' : 'Register a new patient'}</p>
            </div>
            <button className="btn btn-outline" onClick={() => { setShowModal(false); setForm(EMPTY_FORM); setEditId(null); }}>
              <FaArrowLeft /> Back
            </button>
          </div>
          <div className="card" style={{ padding: 24, maxWidth: 700, margin: '0 auto' }}>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Full Name *</label>
                <input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Patient full name" />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Age *</label>
                  <input type="number" required min="0" max="150" value={form.age} onChange={e => setForm(p => ({ ...p, age: e.target.value }))} placeholder="Age" />
                </div>
                <div className="form-group">
                  <label>Gender</label>
                  <select value={form.gender} onChange={e => setForm(p => ({ ...p, gender: e.target.value }))}>
                    <option>Male</option><option>Female</option><option>Other</option>
                  </select>
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Phone *</label>
                  <input required value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="Phone number" />
                </div>
                <div className="form-group">
                  <label>Blood Group</label>
                  <select value={form.bloodGroup} onChange={e => setForm(p => ({ ...p, bloodGroup: e.target.value }))}>
                    {['A+','A-','B+','B-','O+','O-','AB+','AB-'].map(bg => <option key={bg}>{bg}</option>)}
                  </select>
                </div>
              </div>
              {role === 'Admin' && doctors.length > 0 && (
                <div className="form-group">
                  <label>Assign Doctor</label>
                  <select value={form.doctorId} onChange={e => setForm(p => ({ ...p, doctorId: e.target.value }))}>
                    <option value="">No doctor assigned</option>
                    {doctors.map(d => <option key={d.id} value={d.id}>{d.name}{d.specialization ? ` — ${d.specialization}` : ''}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email address" />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} placeholder="Address" />
              </div>
              <div className="form-group">
                <label>Medical History</label>
                <textarea value={form.medicalHistory} onChange={e => setForm(p => ({ ...p, medicalHistory: e.target.value }))} placeholder="Known conditions, allergies..." />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => { setShowModal(false); setForm(EMPTY_FORM); setEditId(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>
                  {saving ? 'Saving...' : editId ? 'Update' : 'Add Patient'}
                </button>
              </div>
            </form>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <Header title="Patients" />
      <Sidebar />
      <main className="page-content">
        <div className="page-header">
          <div>
            <h1><FaUserInjured style={{ color: 'var(--primary)', marginRight: 8 }} />Patients</h1>
            <p>{patients.length} {role === 'Patient' ? 'profile' : 'registered patients'}</p>
          </div>
          {(role === 'Doctor' || role === 'Admin') && (
            <button className="btn btn-primary" onClick={() => { setPickerSelected(''); setShowPicker(true); }}>
              <FaPlus /> Add
            </button>
          )}
        </div>

        <div className="search-bar">
          <FaSearch />
          <input placeholder="Search by name, phone, blood group..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {loading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <FaUserInjured className="empty-state-icon" />
            <h3>No patients found</h3>
          </div>
        ) : (
          <div className="patients-grid">
            {filtered.map(p => (
              <div className="patient-card card" key={p.id || p._id} onClick={() => setViewPatient(p)}>
                <div className="patient-avatar">
                  {p.account?.profileImage ? <img src={getFileUrl(p.account.profileImage)} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : p.name?.charAt(0).toUpperCase()}
                </div>
                <div className="patient-info">
                  <div className="patient-name">{p.name}</div>
                  <div className="patient-meta">
                    <span>{p.age}y • {p.gender}</span>
                    <span className="patient-blood"><FaTint size={10} /> {p.bloodGroup}</span>
                  </div>
                  {p.phone && <div className="patient-phone"><FaPhone size={10} /> {p.phone}</div>}
                  {p.doctorId && (
                    <div className="patient-doctor">
                      <FaUserMd size={10} /> {getDoctorName(p.doctorId) || `Dr. #${p.doctorId}`}
                    </div>
                  )}
                </div>
                {(role === 'Doctor' || role === 'Admin') && (
                  <div className="patient-actions" onClick={e => e.stopPropagation()}>
                    <button className="action-btn action-btn-edit" onClick={() => handleEdit(p)}><FaEdit /></button>
                    <button className="action-btn action-btn-delete" onClick={() => handleDelete(p.id || p._id)}><FaTrash /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
      <BottomNav />

      {showPicker && (
        <div className="modal-overlay center" onClick={e => e.target === e.currentTarget && setShowPicker(false)}>
          <div className="modal-box" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h2>Select Patient</h2>
              <button className="modal-close" onClick={() => setShowPicker(false)}>×</button>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 14 }}>
              {role === 'Doctor'
                ? 'Patients who have booked appointments with you'
                : 'All registered patients'}
            </p>
            <div className="form-group">
              <label>Patient Name</label>
              <select
                value={pickerSelected}
                onChange={e => setPickerSelected(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e8e8f0', fontSize: 14 }}
              >
                <option value="">— Enter manually —</option>
                {registeredPatients.map(p => (
                  <option key={p.id || p._id} value={String(p.id || p._id)}>
                    {p.name}{p.phone ? ` · ${p.phone}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowPicker(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handlePickerConfirm}>
                {pickerSelected ? 'Continue' : 'Add Manually'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewPatient && (
        <div className="modal-overlay center" onClick={e => e.target === e.currentTarget && setViewPatient(null)}>
          <div className="modal-box">
            <div className="modal-header">
              <h2>Patient Details</h2>
              <button className="modal-close" onClick={() => setViewPatient(null)}>×</button>
            </div>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ width: 70, height: 70, background: 'linear-gradient(135deg,#E8500A,#FF6B2C)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 28, fontWeight: 800, margin: '0 auto 10px' }}>
                {viewPatient.name?.charAt(0)}
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700 }}>{viewPatient.name}</h3>
            </div>
            <div className="info-row"><span className="info-label">Age</span><span className="info-value">{viewPatient.age} years</span></div>
            <div className="info-row"><span className="info-label">Gender</span><span className="info-value">{viewPatient.gender}</span></div>
            <div className="info-row"><span className="info-label">Blood Group</span><span className="info-value">{viewPatient.bloodGroup}</span></div>
            <div className="info-row"><span className="info-label">Phone</span><span className="info-value">{viewPatient.phone}</span></div>
            <div className="info-row"><span className="info-label">Email</span><span className="info-value">{viewPatient.email || '-'}</span></div>
            <div className="info-row"><span className="info-label">Address</span><span className="info-value">{viewPatient.address || '-'}</span></div>
            <div className="info-row"><span className="info-label">Medical History</span><span className="info-value">{viewPatient.medicalHistory || 'None'}</span></div>
            <div className="info-row">
              <span className="info-label">Assigned Doctor</span>
              <span className="info-value">
                {viewPatient.doctorId ? (getDoctorName(viewPatient.doctorId) || `Dr. #${viewPatient.doctorId}`) : 'Not assigned'}
              </span>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .patients-grid { display: flex; flex-direction: column; gap: 10px; }
        .patient-card { display: flex; align-items: center; gap: 14px; cursor: pointer; padding: 14px 16px; }
        .patient-avatar { width: 48px; height: 48px; background: linear-gradient(135deg,#E8500A,#FF6B2C); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 20px; font-weight: 700; flex-shrink: 0; }
        .patient-info { flex: 1; min-width: 0; }
        .patient-name { font-size: 15px; font-weight: 700; color: var(--text-dark); }
        .patient-meta { display: flex; gap: 12px; font-size: 12px; color: var(--text-light); margin-top: 3px; }
        .patient-blood { color: #e53935; font-weight: 600; }
        .patient-phone { font-size: 12px; color: var(--text-light); margin-top: 2px; }
        .patient-doctor { font-size: 12px; color: var(--primary); margin-top: 2px; }
        .patient-actions { display: flex; gap: 6px; flex-shrink: 0; }
        .spinner-mini { width: 14px; height: 14px; border: 2px solid #ccc; border-top-color: var(--primary); border-radius: 50%; animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default Patients;
